import { type ReactNode } from 'react';
import { type LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Card, CardContent } from '@/ui/base/card';
import { cn } from '@/nucleo/utilitarios';

/**
 * Variante de `@/ui/StatCard` com tom semântico (atenção/crítico/positivo).
 * O componente do design system não tem esse conceito hoje — aqui ele é
 * necessário porque o painel precisa sinalizar, de relance, quais dos ~13
 * indicadores pedem ação do gestor agora. Sempre por token semântico
 * (text-warning, text-destructive, text-success, text-primary).
 */

export type TomIndicador = 'neutro' | 'atencao' | 'critico' | 'positivo';

const TOM_CLASSE: Record<TomIndicador, string> = {
  neutro: 'text-primary',
  atencao: 'text-warning',
  critico: 'text-destructive',
  positivo: 'text-success',
};

interface CartaoIndicadorProps {
  titulo: string;
  valor: string | number;
  descricao?: ReactNode;
  icone: LucideIcon;
  tom?: TomIndicador;
  to?: string;
  aoClicar?: () => void;
}

export function CartaoIndicador({
  titulo,
  valor,
  descricao,
  icone: Icone,
  tom = 'neutro',
  to,
  aoClicar,
}: CartaoIndicadorProps) {
  const interativo = Boolean(to || aoClicar);

  const conteudo = (
    <Card
      className={cn(
        'h-full rounded-2xl border border-border/60',
        interativo && 'cursor-pointer transition-all hover:border-primary/40 hover:shadow-md',
      )}
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {titulo}
            </p>
            <p className="mt-1 truncate text-2xl font-bold tabular-nums">{valor}</p>
            {descricao && <p className="mt-1 text-xs text-muted-foreground">{descricao}</p>}
          </div>
          <Icone className={cn('h-5 w-5 shrink-0', TOM_CLASSE[tom])} aria-hidden />
        </div>
      </CardContent>
    </Card>
  );

  if (to) {
    return (
      <Link to={to} className="block h-full" aria-label={titulo}>
        {conteudo}
      </Link>
    );
  }
  if (aoClicar) {
    return (
      <button type="button" onClick={aoClicar} className="block h-full w-full text-left">
        {conteudo}
      </button>
    );
  }
  return conteudo;
}
